import { Shell } from "@/components/Shell";
import { BackgroundBeams } from "@/components/ui/background-beams";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { authClient } from "@/lib/auth-client";
import { useMutation } from "@tanstack/react-query";
import { LockIcon } from "lucide-react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const schema = z
	.object({
		newPassword: z
			.string("Password is required")
			.min(8, "Password must be at least 8 characters long"),
		confirmNewPassword: z
			.string("Confirm password is required")
			.min(8, "Confirm password must be at least 8 characters long"),
	})
	.refine((data) => data.newPassword === data.confirmNewPassword, {
		message: "Passwords do not match",
		path: ["confirmNewPassword"],
	});

export function ResetPassword() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const navigate = useNavigate();
	const { mutate: resetPassword, isPending } = useMutation({
		mutationFn: async ({
			newPassword,
			token,
		}: {
			newPassword: string;
			token: string;
		}) => {
			const result = await authClient.resetPassword({
				newPassword,
				token,
			});
			if (result.error) {
				throw new Error(result.error.message ?? "Failed to reset password");
			}
			return result.data;
		},
		onSuccess: () => {
			toast.success("Password reset successfully");
			navigate("/sign-in");
		},
		onError: (error) => {
			toast.error(error.message ?? "Failed to reset password");
		},
	});
	const form = useForm<z.infer<typeof schema>>({
		validators: {
			onSubmit: schema,
		},
		defaultValues: {
			newPassword: "",
			confirmNewPassword: "",
		},
		onSubmit: async ({ value }) => {
			resetPassword({
				newPassword: value.newPassword,
				token: token ?? "",
			});
		},
	});

	if (!token) {
		return <Navigate to="/sign-in" replace />;
	}

	return (
		<Shell className="flex items-center justify-center h-dvh">
			<BackgroundBeams />
			<Card className="w-full max-w-md z-1 bg-card/20">
				<CardHeader>
					<CardTitle>Reset Password</CardTitle>
					<CardDescription>Enter your new password</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						id="reset-password"
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
					>
						<FieldGroup>
							<FieldSet>
								<form.Field name="newPassword">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="newPassword">
												New Password
											</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<LockIcon />
												</InputGroupAddon>
												<InputGroupInput
													id="newPassword"
													type="password"
													placeholder="•••••••••"
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													required
													disabled={isPending}
													className="h-8"
												/>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="confirmNewPassword">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="confirmNewPassword">
												Confirm Password
											</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<LockIcon />
												</InputGroupAddon>
												<InputGroupInput
													id="confirmNewPassword"
													type="password"
													placeholder="•••••••••"
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													required
													disabled={isPending}
													className="h-8"
												/>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
							</FieldSet>
						</FieldGroup>
					</form>
				</CardContent>
				<CardFooter className="flex flex-col gap-4">
					<form.Subscribe
						selector={(state) => [
							state.isSubmitting,
							state.isValid,
							state.isDirty,
						]}
					>
						{([isSubmitting, isValid, isDirty]) => (
							<Button
								type="submit"
								form="reset-password"
								className="w-full"
								disabled={!isValid || !isDirty}
								loading={isSubmitting || isPending}
							>
								Reset Password
							</Button>
						)}
					</form.Subscribe>
					<p className="text-sm text-muted-foreground">
						<Link to="/sign-in" className="text-primary hover:underline">
							Back to sign in
						</Link>
					</p>
				</CardFooter>
			</Card>
		</Shell>
	);
}
